import { Alert, Button, Card, Center, List, Loader, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';

import { AppShell } from '../../components/app-shell/AppShell';
import { MotionSection, PageHero } from '../../components/ui';
import { JourneyStrip } from '../../components/wizard/JourneyStrip';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders } from '../../lib/auth-context';
import { useStripeSubscriptionSync, useVenueBillingStatus } from '../../lib/venue-subscription';

/**
 * /onboarding/host — the subscribe step of venue sign-up.
 *
 * Venues subscribe straight after the account form (owner decision,
 * 2026-09-26): sign-up → here → Stripe Checkout → back here → venue details
 * (/host/venues/new). The server enforces the same rule (402 on venue create /
 * submit, worker lib/venue-subscription.ts); this page is the friendly front
 * door to it. When the server says no subscription is needed (already
 * subscribed, or payments aren't switched on for this account) the page steps
 * straight through to venue details.
 *
 * This path used to be a retired redirect to /host/venues/new, so old links
 * still land somewhere sensible.
 */

type Search = { checkout?: 'success' | 'cancelled' };

export const Route = createFileRoute('/onboarding/host')({
  validateSearch: (s: Record<string, unknown>): Search =>
    s.checkout === 'success' || s.checkout === 'cancelled' ? { checkout: s.checkout } : {},
  component: HostSubscribeStep,
});

function HostSubscribeStep() {
  const { checkout } = Route.useSearch();
  const status = useVenueBillingStatus({ pollWhileRequired: checkout === 'success' });
  // Ask Stripe directly while waiting, rather than wait for the 15-minute event poll.
  useStripeSubscriptionSync(checkout === 'success' && status.data?.subscription_required === true);

  const start = useMutation({
    mutationFn: async () => {
      const r = await fetchOk('/api/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ returnTo: 'onboarding' }),
      });
      const j = (await r.json()) as { checkoutUrl?: unknown };
      if (typeof j.checkoutUrl !== 'string') throw new Error('Billing is unavailable right now. Try again shortly.');
      window.location.assign(j.checkoutUrl);
    },
    onError: (err) => {
      // 409 = already subscribed (e.g. a second tab): nothing to pay, carry on.
      if (err instanceof ApiError && err.status === 409) void status.refetch();
    },
  });

  if (status.isLoading) {
    return (
      <AppShell tone="form">
        <Center mih={240}>
          <Loader />
        </Center>
      </AppShell>
    );
  }

  // Subscribed, or payments aren't on for this account: straight to venue details.
  if (status.data !== undefined && status.data.subscription_required !== true) {
    return <Navigate to="/host/venues/new" replace />;
  }

  const price = status.data?.monthly_price_aud;
  const waitingForStripe = checkout === 'success';

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <JourneyStrip journey="host" current="subscribe" />
        <MotionSection>
          <PageHero
            eyebrow="Venue sign-up"
            title="Start your subscription"
            lede="One flat monthly subscription for your venue. No booking fees, no commission, ever."
          />
        </MotionSection>

        {status.isError ? (
          <Alert color="red" variant="light">
            We couldn&apos;t check your subscription. Refresh the page to try again.
          </Alert>
        ) : null}

        {checkout === 'cancelled' ? (
          <Alert color="gray" variant="light">
            Checkout was cancelled. Nothing was charged. Start again whenever you&apos;re ready.
          </Alert>
        ) : null}

        <Card withBorder padding="lg">
          {waitingForStripe ? (
            <Stack align="center" gap="sm" py="md">
              <Loader />
              <Text ta="center">Payment received. Setting up your subscription…</Text>
              <Text size="sm" c="dimmed" ta="center">
                This usually takes a few seconds. You&apos;ll move on to your venue details automatically.
              </Text>
            </Stack>
          ) : (
            <Stack gap="md">
              <Text fw={700} size="lg">
                {price !== undefined ? `A$${price} a month` : 'Monthly subscription'}
              </Text>
              <List spacing="xs" size="sm">
                <List.Item>List your venue and open your calendar to touring artists</List.Item>
                <List.Item>Artists send requests straight to you. You choose who to host.</List.Item>
                <List.Item>
                  Charged on the 1st of each month. Today you pay only for the days left in this month.
                </List.Item>
                <List.Item>Cancel any time from Billing</List.Item>
              </List>
              {start.isError && !(start.error instanceof ApiError && start.error.status === 409) ? (
                <Alert color="red" variant="light">
                  {start.error.message}
                </Alert>
              ) : null}
              <Button size="md" onClick={() => start.mutate()} loading={start.isPending}>
                Continue to payment
              </Button>
              <Text size="xs" c="dimmed">
                You&apos;ll pay securely through Stripe, then come straight back to set up your venue:
                a few quick questions, about ten minutes.
              </Text>
            </Stack>
          )}
        </Card>
      </Stack>
    </AppShell>
  );
}
